import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { FaBook } from "react-icons/fa";
import { getAbsoluteUrl } from "../../api/urls";
import {
  getWebhookEndpointList,
  testWebhookEndpoint,
  WebhookEndpointTestResult,
} from "../../api/webhook-endpoints";
import WebhookEndpoint from "../../models/webhook-endpoint";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import WebhookEndpointCreateModal from "./WebhookEndpointCreateModal";
import WebhookEndpointEditModal from "./WebhookEndpointEditModal";
import WebhookEndpointDeleteModal from "./WebhookEndpointDeleteModal";
import WebhookEndpointCrudActions from "./WebhookEndpointCrudActions";
import WebhookEndpointRawSecretModal from "./WebhookEndpointRawSecretModal";
import WebhookEndpointEventLog from "./WebhookEndpointEventLog";

const WebhookEndpointCrud = () => {
  const fields = [
    { key: "name", _classes: ["text-center"], label: "Nombre" },
    { key: "url", _classes: ["text-center"], label: "URL" },
    { key: "isActive", _classes: ["text-center"], label: "Activo" },
    { key: "eventTypes", _classes: ["text-center"], label: "Eventos" },
    { key: "createdAt", _classes: ["text-center"], label: "Creado" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRawSecretModal, setShowRawSecretModal] = useState(false);
  const [showEventLog, setShowEventLog] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<
    WebhookEndpoint | undefined
  >(undefined);
  const [newRawSecret, setNewRawSecret] = useState("");
  const [newEndpointName, setNewEndpointName] = useState("");

  const [testingEndpointId, setTestingEndpointId] = useState<
    number | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);

  const fetchEndpoints = useCallback(async () => {
    setLoading(true);
    const status = await getWebhookEndpointList(taxPayer.id!);
    if (status.status === SUCCESS) {
      if (status.data !== undefined) {
        setEndpoints(status.data.items);
      }
    } else {
      const message = status.detail ?? "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  }, [taxPayer]);

  const onAddClick = () => {
    setShowCreateModal(true);
  };

  const onEditClick = (endpoint: WebhookEndpoint) => {
    setSelectedEndpoint(endpoint);
    setShowEditModal(true);
  };

  const onDeleteClick = (endpoint: WebhookEndpoint) => {
    setSelectedEndpoint(endpoint);
    setShowDeleteModal(true);
  };

  const onViewEventsClick = (endpoint: WebhookEndpoint) => {
    setSelectedEndpoint(endpoint);
    setShowEventLog(true);
  };

  const onTestClick = async (endpoint: WebhookEndpoint) => {
    if (endpoint.id === undefined) {
      return;
    }

    setTestingEndpointId(endpoint.id);

    const status = await testWebhookEndpoint(endpoint.id);

    if (status.status !== SUCCESS) {
      errorAlert(status.detail ?? "No se pudo enviar el evento de prueba");
    } else {
      const result = status.data as unknown as WebhookEndpointTestResult;

      if (result.delivered) {
        successAlert(
          `El receptor respondió ${result.responseStatus}. La entrega funcionó.`
        );
      } else {
        warningAlert(
          `El receptor respondió ${
            result.responseStatus ?? "sin conexión"
          }: ${result.responseBody}`
        );
      }
    }

    setTestingEndpointId(undefined);
  };

  const onCreateCancel = () => {
    setShowCreateModal(false);
  };

  const onCreateSuccess = (created?: WebhookEndpoint) => {
    setShowCreateModal(false);
    if (created && (created as any).rawSecret) {
      setNewRawSecret((created as any).rawSecret);
      setNewEndpointName(created.name ?? "");
      setShowRawSecretModal(true);
    } else {
      successAlert("Webhook creado con éxito!");
    }
    fetchEndpoints();
  };

  const onEditCancel = () => {
    setShowEditModal(false);
    setSelectedEndpoint(undefined);
  };

  const onEditSuccess = () => {
    successAlert("Webhook actualizado con éxito!");
    setShowEditModal(false);
    setSelectedEndpoint(undefined);
    fetchEndpoints();
  };

  const onDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedEndpoint(undefined);
  };

  const onDeleteSuccess = () => {
    successAlert("Webhook eliminado con éxito!");
    setShowDeleteModal(false);
    setSelectedEndpoint(undefined);
    fetchEndpoints();
  };

  const onRawSecretModalClose = () => {
    setShowRawSecretModal(false);
    setNewRawSecret("");
    setNewEndpointName("");
  };

  const onEventLogClose = () => {
    setShowEventLog(false);
    setSelectedEndpoint(undefined);
  };

  useEffect(() => {
    fetchEndpoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const docsUrl = getAbsoluteUrl("/api/v2/webhooks/docs");

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CAlert color="info" className="d-flex align-items-center">
            <FaBook className="mr-2 flex-shrink-0" />
            <span>
              Recibí notificaciones automáticas de eventos en tu sistema.{" "}
              <a href={docsUrl} target="_blank" rel="noopener noreferrer">
                Ver documentación de los Webhooks
              </a>
            </span>
          </CAlert>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2">
                <h3>Webhooks</h3>
              </div>
              <div className="p-2">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                  &nbsp; Nuevo Webhook
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                noItemsView={{
                  noItems: "No se encontraron webhooks",
                  noResults: "No se encontraron webhooks",
                }}
                addTableClasses={"table-fixed"}
                fields={fields}
                items={endpoints}
                striped
                border
                loading={loading}
                responsive
                scopedSlots={{
                  name: (item: WebhookEndpoint) => (
                    <td className="text-center">{item.name ?? "-"}</td>
                  ),
                  url: (item: WebhookEndpoint) => (
                    <td
                      className="text-center"
                      style={{
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={item.url}
                    >
                      {item.url ?? "-"}
                    </td>
                  ),
                  isActive: (item: WebhookEndpoint) => (
                    <td className="text-center">
                      <CBadge color={item.isActive ? "success" : "secondary"}>
                        {item.isActive ? "Activo" : "Inactivo"}
                      </CBadge>
                    </td>
                  ),
                  eventTypes: (item: WebhookEndpoint) => (
                    <td className="text-center">
                      {item.eventTypes && item.eventTypes.length > 0
                        ? `${item.eventTypes.length} evento(s)`
                        : "-"}
                    </td>
                  ),
                  createdAt: (item: WebhookEndpoint) => (
                    <td className="text-center">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                  ),
                  actions: (item: WebhookEndpoint) => (
                    <WebhookEndpointCrudActions
                      endpoint={item}
                      onEditClick={onEditClick}
                      onDeleteClick={onDeleteClick}
                      onViewEventsClick={onViewEventsClick}
                      onTestClick={onTestClick}
                      testingEndpointId={testingEndpointId}
                    />
                  ),
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <WebhookEndpointCreateModal
        show={showCreateModal}
        onCancel={onCreateCancel}
        onSuccess={onCreateSuccess}
      />
      <WebhookEndpointEditModal
        show={showEditModal}
        endpoint={selectedEndpoint}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
      />
      <WebhookEndpointDeleteModal
        show={showDeleteModal}
        endpoint={selectedEndpoint}
        onCancel={onDeleteCancel}
        onSuccess={onDeleteSuccess}
      />
      <WebhookEndpointRawSecretModal
        show={showRawSecretModal}
        rawSecret={newRawSecret}
        endpointName={newEndpointName}
        onClose={onRawSecretModalClose}
      />
      <WebhookEndpointEventLog
        show={showEventLog}
        endpointId={selectedEndpoint?.id}
        endpointName={selectedEndpoint?.name}
        onClose={onEventLogClose}
      />
    </>
  );
};

export default WebhookEndpointCrud;
